import {
  EMPTY_SCREENING_AVAILABILITY,
  SCREEN_BREAK_MINUTES,
  SCREEN_LENGTH_MINUTES,
} from "@/lib/constants";
import {
  AvailabilityTimeRange,
  CandidacyStatus,
  DayOfWeek,
  ScreeningAvailability,
} from "@/types";

const parseTimeToMinutes = (hhmm: string): number => {
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return Number.NaN;
  return h * 60 + m;
};

// Mastra books only on midnight-anchored 20-min marks (e.g. :00, :20, :40).
// Count slot starts within the window where `slotStart + screenLength <= end`.
const capacityForRange = (range: AvailabilityTimeRange): number => {
  const start = parseTimeToMinutes(range.startTime);
  const end = parseTimeToMinutes(range.endTime);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  const interval = SCREEN_LENGTH_MINUTES + SCREEN_BREAK_MINUTES;
  const firstSlot = Math.ceil(start / interval) * interval;
  const lastSlot = end - SCREEN_LENGTH_MINUTES;
  if (firstSlot > lastSlot) return 0;
  return Math.floor((lastSlot - firstSlot) / interval) + 1;
};

// Coalesce overlapping / touching ranges so overlap doesn't double-count
// capacity (e.g. 9-11 + 10-12 should count as one 9-12 window).
const mergeOverlapping = (
  ranges: AvailabilityTimeRange[],
): AvailabilityTimeRange[] => {
  const valid = ranges.filter((r) => {
    const s = parseTimeToMinutes(r.startTime);
    const e = parseTimeToMinutes(r.endTime);
    return Number.isFinite(s) && Number.isFinite(e) && e > s;
  });
  const sorted = [...valid].sort((a, b) =>
    a.startTime.localeCompare(b.startTime),
  );
  const merged: AvailabilityTimeRange[] = [];
  for (const r of sorted) {
    const last = merged.at(-1);
    if (!last) {
      merged.push(r);
      continue;
    }
    if (parseTimeToMinutes(r.startTime) <= parseTimeToMinutes(last.endTime)) {
      if (parseTimeToMinutes(r.endTime) > parseTimeToMinutes(last.endTime)) {
        merged[merged.length - 1] = { ...last, endTime: r.endTime };
      }
    } else {
      merged.push(r);
    }
  }
  return merged;
};

// Sum capacity across the day's ranges, merging overlaps first so
// overlapping windows don't double-count.
const capacityForDay = (ranges: AvailabilityTimeRange[]): number =>
  mergeOverlapping(ranges).reduce((acc, r) => acc + capacityForRange(r), 0);

const setDayRanges = (
  availability: ScreeningAvailability,
  day: DayOfWeek,
  ranges: AvailabilityTimeRange[],
): ScreeningAvailability => ({ ...availability, [day]: ranges });

// Calendly-style copy: target days' ranges are REPLACED with clones of
// the source day's ranges (not merged), matching the prototype.
const copyDayRangesTo = (
  availability: ScreeningAvailability,
  sourceDay: DayOfWeek,
  targetDays: DayOfWeek[],
): ScreeningAvailability => {
  const source = availability[sourceDay];
  const next: ScreeningAvailability = { ...availability };
  for (const day of targetDays) {
    next[day] = source.map((r) => ({ ...r }));
  }
  return next;
};

// Null means "never configured" — fall back to all-empty so consumers
// don't have to guard.
const fromServer = (
  value: ScreeningAvailability | null,
): ScreeningAvailability => value ?? EMPTY_SCREENING_AVAILABILITY;

// Mirrors guac's hasConfiguredAvailability: never saved OR every day empty
// both count as not configured.
const isConfigured = (value: ScreeningAvailability | null): boolean =>
  value !== null && Object.values(value).some((ranges) => ranges.length > 0);

// Block shortlisting if a user's calendar hasn't been configured
const blocksShortlist = ({
  candidacyStatus,
  jobAgentActive,
  availability,
}: {
  candidacyStatus: CandidacyStatus;
  jobAgentActive: boolean;
  availability: ScreeningAvailability | null;
}): boolean =>
  candidacyStatus === CandidacyStatus.APPLIED &&
  jobAgentActive &&
  !isConfigured(availability);

export const screeningAvailability = {
  capacityForRange,
  capacityForDay,
  setDayRanges,
  copyDayRangesTo,
  fromServer,
  isConfigured,
  blocksShortlist,
};
